import { useMemo } from 'react';
import type { Column, Rec } from '../lib/api';
import type { Computed } from '../lib/formula/engine';
import { R, colLabel, ft, mono, plural } from '../lib/ui';
import type { Sel } from './Grid';

/** Целое — как есть, дробное — до двух знаков без хвостовых нулей. */
function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

export interface Stats {
  range: string;
  cells: number;
  filled: number;
  unique: number;
  sum: number | null;
  min: string | null;
  max: string | null;
}

/**
 * Итоги по выделению. Правила из оригинала, и они неочевидные:
 * сравнение всегда строковое через localeCompare с numeric, а сумма и
 * мин/макс показываются только для выделения внутри одной колонки —
 * складывать разнородные колонки смысла нет.
 */
const collator = new Intl.Collator('ru', { numeric: true });

export function useSelectionStats(
  sel: Sel | null,
  columns: Column[],
  records: Rec[],
  computed?: Map<string, Computed>,
): Stats | null {
  return useMemo(() => {
    if (!sel) return null;

    const cells = (sel.r2 - sel.r1 + 1) * (sel.c2 - sel.c1 + 1);
    if (cells <= 1) return null;

    const singleColumn = sel.c1 === sel.c2;
    const unique = new Set<string>();
    let filled = 0;
    let sum = 0;
    let numeric = 0;
    let min: string | null = null;
    let max: string | null = null;

    for (let r = sel.r1; r <= sel.r2; r++) {
      const rec = records[r];
      if (!rec) continue;

      for (let c = sel.c1; c <= sel.c2; c++) {
        const col = columns[c];
        if (!col) continue;

        const calc = computed?.get(`${rec.id}:${col.fields.colId}`);
        const raw = calc ? calc.value : rec.fields[col.fields.colId];
        const text = raw === null || raw === undefined ? '' : String(raw);
        if (text === '') continue;

        filled++;
        unique.add(text);

        const n = Number(text.replace(',', '.'));
        if (!Number.isNaN(n) && text.trim() !== '') {
          sum += n;
          numeric++;
        }

        if (singleColumn) {
          if (min === null || collator.compare(text, min) < 0) min = text;
          if (max === null || collator.compare(text, max) > 0) max = text;
        }
      }
    }

    // Мин и макс — только если колонка однородная: либо всё числа, либо ничего.
    const homogeneous = singleColumn && (numeric === 0 || numeric === filled);

    return {
      range: `${colLabel(sel.c1)}${sel.r1 + 1}:${colLabel(sel.c2)}${sel.r2 + 1}`,
      cells,
      filled,
      unique: unique.size,
      sum: singleColumn && numeric > 0 && numeric === filled ? sum : null,
      min: homogeneous ? min : null,
      max: homogeneous ? max : null,
    };
  }, [sel, columns, records, computed]);
}

export function StatusBar({
  stats, rowCount, colCount, editorsOnline = 0,
}: {
  stats: Stats | null;
  rowCount: number;
  colCount: number;
  editorsOnline?: number;
}) {
  return (
    <div
      className="rt-scroll"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        minHeight: 38,
        padding: '0 20px',
        background: R.bgSubtle,
        borderTop: ft.strong,
        fontSize: 11.5,
        fontFamily: mono,
        color: R.textMuted,
        flex: '0 0 auto',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
      }}
    >
      {stats ? (
        <>
          <span style={{ color: R.text }}>{stats.range}</span>
          <span>
            {stats.cells} {plural(stats.cells, 'ячейка', 'ячейки', 'ячеек')}
          </span>
          <span>заполнено {stats.filled}</span>
          <span>уникальных {stats.unique}</span>
          {stats.sum !== null && <span>сумма {formatNumber(stats.sum)}</span>}
          {stats.min !== null && <span>мин {stats.min}</span>}
          {stats.max !== null && <span>макс {stats.max}</span>}
        </>
      ) : (
        <span>
          {rowCount} {plural(rowCount, 'строка', 'строки', 'строк')} · {colCount}{' '}
          {plural(colCount, 'колонка', 'колонки', 'колонок')}
        </span>
      )}

      <span style={{ marginLeft: 'auto' }}>
        {editorsOnline > 0
          ? `${editorsOnline} ${plural(editorsOnline, 'редактор', 'редактора', 'редакторов')} онлайн`
          : 'только вы'}
      </span>
    </div>
  );
}
